import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Wifi } from 'lucide-react';
import { motion } from 'framer-motion';
import AddressSearchComponent from './AddressSearchComponent';
import NavItemsComponent from './NavItemsComponent';

/**
 * DesktopNavComponent - Renders desktop navigation UI
 * 
 * This component handles the desktop navigation bar with logo, menu items, and actions.
 * Enhanced with refined visual design and micro-interactions.
 */
const DesktopNavComponent = ({
  navItems,
  activeMenu,
  toggleMenu,
  searchContainerRef,
  searchInputRef,
  searchQuery,
  setSearchQuery,
  setIsAddressSelected,
  isLoadingGeolocation,
  handleClearSearch,
  searchResults,
  isSearching,
  handleAddressSelect,
  isCheckingCoverage,
  openCoverageSidebar,
  isHidden = false
}) => {
  return (
    <motion.nav
      className="fixed w-full z-50 hidden md:block"
      initial={{ y: 0, opacity: 1 }}
      animate={{
        y: isHidden ? -100 : 0,
        opacity: 1 // Always keep opacity at 1 for a pure slide effect
      }}
      transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1], // Custom cubic-bezier for professional easing
        opacity: { duration: 0.6 }
      }}
    >
      {/* Enhanced Navigation Bar with subtle texture and refined gradient */}
      <div style={{ backgroundColor: '#121212' }} className="shadow-xl border-b border-gray-800/50 relative">
        {/* Subtle top highlight */}
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-purple-500/20 to-transparent"></div>
        
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            {/* Logo with enhanced hover effect */}
            <Link to="/" className="flex-shrink-0 transition-all duration-300 hover:scale-105 relative group">
              <div className="absolute -inset-2 bg-gradient-to-r from-purple-600/0 to-purple-600/0 group-hover:from-purple-600/5 group-hover:to-purple-600/0 rounded-lg blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <img
                src="https://apextech.group/wondernet-logo-white.png"
                alt="Wondernet Logo"
                className="h-8 w-auto relative"
              />
            </Link>
            
            {/* Main Navigation with refined styling */}
            <div className="hidden md:flex items-center space-x-2">
              {navItems.map((item, index) => (
                <div key={index} className="relative group">
                  {item.path ? (
                    <Link
                      to={item.path}
                      className="px-3 py-2 text-gray-300 hover:text-white rounded-md text-sm font-medium transition-colors duration-300 relative flex items-center group"
                    >
                      <span>{item.name}</span>
                      {/* Animated underline with refined transition */}
                      <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-500 to-purple-400 group-hover:w-full transition-all duration-300 rounded-full"></span>
                    </Link>
                  ) : (
                    <button
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-300 flex items-center space-x-1.5 relative group ${
                        activeMenu === item.menuId
                          ? 'text-white'
                          : item.name === 'New'
                            ? 'text-purple-300 hover:text-white' // Highlight the new menu item
                            : 'text-gray-300 hover:text-white'
                      }`}
                      onClick={() => toggleMenu(item.menuId)}
                    >
                      <span>{item.name}</span>
                      
                      {/* Enhanced "New" badge */}
                      {item.name === 'New' && (
                        <span className="absolute -top-1.5 -right-1.5 bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white text-[10px] px-1.5 py-0.5 rounded-full shadow-sm font-semibold">New</span>
                      )}
                      
                      {/* Enhanced chevron with smoother rotation */}
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-300 text-gray-400 group-hover:text-gray-200 ${
                          activeMenu === item.menuId ? 'rotate-180 text-purple-300' : ''
                        }`}
                      />
                      
                      {/* Animated underline with gradient */}
                      <span 
                        className={`absolute bottom-0 left-0 w-0 h-0.5 rounded-full
                          ${activeMenu === item.menuId 
                            ? 'w-full bg-gradient-to-r from-purple-500 to-purple-400' 
                            : item.name === 'New' 
                              ? 'bg-gradient-to-r from-purple-500 to-fuchsia-500 group-hover:w-full' 
                              : 'bg-gradient-to-r from-purple-500 to-purple-400 group-hover:w-full'
                          } transition-all duration-300`}
                      ></span>
                    </button>
                  )}
                </div>
              ))}
            </div>
            
            {/* Address Search */}
            <AddressSearchComponent
              searchContainerRef={searchContainerRef}
              searchInputRef={searchInputRef}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              setIsAddressSelected={setIsAddressSelected}
              isLoadingGeolocation={isLoadingGeolocation}
              handleClearSearch={handleClearSearch}
              searchResults={searchResults}
              isSearching={isSearching}
              handleAddressSelect={handleAddressSelect}
            />
            
            {/* Right Side Actions */}
            <NavItemsComponent 
              isCheckingCoverage={isCheckingCoverage}
              openCoverageSidebar={openCoverageSidebar}
            />
          </div>
        </div>
      </div>
    </motion.nav>
  );
};

export default DesktopNavComponent;